// Weekly scheduled-hours math. Hours attribute to the week of the shift's
// START day (weekId is derived from `date`), so summing by weekId is the
// whole attribution rule. Cancelled shifts never count.

export interface HoursShift {
  assigneeEmail: string | null
  status: 'assigned' | 'open' | 'cancelled'
  durationHours: number
  date: string
}

export type OtState = 'ok' | 'approaching' | 'over'

const APPROACHING_MARGIN = 4 // within 4h of the threshold shows the sunset warning

export function countsTowardHours(s: Pick<HoursShift, 'status' | 'assigneeEmail'>): boolean {
  return s.status === 'assigned' && s.assigneeEmail != null
}

/** Total assigned hours per employee (email → hours), rounded to 2 places. */
export function hoursByEmployee(shifts: HoursShift[]): Map<string, number> {
  const totals = new Map<string, number>()
  for (const s of shifts) {
    if (!countsTowardHours(s)) continue
    const email = s.assigneeEmail!
    totals.set(email, round2((totals.get(email) ?? 0) + s.durationHours))
  }
  return totals
}

/** Total scheduled hours per date (open + assigned; matches the Homebase header row). */
export function hoursByDay(shifts: HoursShift[]): Map<string, number> {
  const totals = new Map<string, number>()
  for (const s of shifts) {
    if (s.status === 'cancelled') continue
    totals.set(s.date, round2((totals.get(s.date) ?? 0) + s.durationHours))
  }
  return totals
}

export function otState(totalHours: number, thresholdHours: number): OtState {
  if (totalHours > thresholdHours) return 'over'
  if (totalHours > thresholdHours - APPROACHING_MARGIN) return 'approaching'
  return 'ok'
}

/** Would adding deltaHours push this employee's week across the threshold? */
export function crossesThreshold(
  currentHours: number,
  deltaHours: number,
  thresholdHours: number,
): boolean {
  return currentHours <= thresholdHours && currentHours + deltaHours > thresholdHours
}

export function round2(n: number): number {
  return Math.round(n * 100) / 100
}
